import React from 'react'
import { Calendar, Clock } from 'lucide-react'
import { format, parseISO, differenceInDays } from 'date-fns'
import { ptBR } from 'date-fns/locale'
import type { VacationBalance } from '@/types/vacation'

interface VacationBalanceCardProps {
  balance: VacationBalance
  onRequestVacation?: () => void
  className?: string
}

export const VacationBalanceCard: React.FC<VacationBalanceCardProps> = ({
  balance,
  onRequestVacation,
  className = ''
}) => {
  const daysUntilExpiration = differenceInDays(
    parseISO(balance.expirationDate),
    new Date()
  )

  const totalDays = balance.totalDaysEarned || balance.daysEarned
  const usagePercentage = totalDays > 0
    ? ((balance.daysUsed + balance.daysScheduled) / totalDays) * 100
    : 0

  const isExpiringSoon = daysUntilExpiration < 30
  const hasHighBalance = balance.daysAvailable > 30

  return (
    <div className={`card-hover ${className}`}>
      <div className="p-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <h3 className="font-display font-semibold text-white text-xl flex items-center gap-2">
            <Calendar className="w-6 h-6 text-brand-primary" />
            Saldo de Férias
          </h3>
          <span className="text-sm text-brand-gray">
            Período {balance.periodYear}
          </span>
        </div>

        {/* Main Balance Display */}
        <div className="text-center mb-6">
          <div className="text-5xl font-display font-bold text-brand-primary mb-2">
            {balance.daysAvailable}
          </div>
          <div className="text-lg text-brand-gray">
            dias disponíveis
          </div>
        </div>

        {/* Progress Bar */}
        <div className="mb-6">
          <div className="flex justify-between text-sm text-brand-gray mb-2">
            <span>Utilização</span>
            <span>{Math.round(usagePercentage)}%</span>
          </div>
          <div className="w-full bg-brand-dark/50 rounded-full h-3 overflow-hidden">
            <div
              className="bg-gradient-to-r from-brand-primary to-blue-600 h-full rounded-full transition-all duration-800"
              style={{ width: `${Math.min(usagePercentage, 100)}%` }}
              role="progressbar"
              aria-valuenow={Math.round(usagePercentage)}
              aria-valuemin={0}
              aria-valuemax={100}
            />
          </div>
        </div>

        {/* Breakdown */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <div className="bg-brand-dark/50 rounded-monumenta p-4">
            <p className="text-sm text-brand-gray mb-1">Total</p>
            <p className="text-3xl font-display font-bold text-white">
              {totalDays}
            </p>
          </div>
          <div className="bg-brand-dark/50 rounded-monumenta p-4">
            <p className="text-sm text-brand-gray mb-1">Usados</p>
            <p className="text-3xl font-display font-bold text-success">
              {balance.daysUsed}
            </p>
          </div>
          <div className="bg-brand-dark/50 rounded-monumenta p-4">
            <p className="text-sm text-brand-gray mb-1">Agendados</p>
            <p className="text-3xl font-display font-bold text-warning">
              {balance.daysScheduled}
            </p>
          </div>
        </div>

        {/* Period Info */}
        <div className="bg-brand-dark-lighter/50 rounded-monumenta p-4 mb-4 space-y-2">
          <div className="flex items-center justify-between text-sm">
            <span className="text-brand-gray">Período aquisitivo</span>
            <span className="font-medium text-white">
              {format(parseISO(balance.acquisitionStart || balance.acquisitionStartDate), 'dd/MM/yyyy', { locale: ptBR })}
              {' - '}
              {format(parseISO(balance.acquisitionEnd || balance.acquisitionEndDate), 'dd/MM/yyyy', { locale: ptBR })}
            </span>
          </div>
          <div className="flex items-center justify-between text-sm">
            <span className="flex items-center gap-1 text-brand-gray">
              <Clock className="w-4 h-4" />
              Expira em
            </span>
            <span className={`font-semibold ${
              isExpiringSoon ? 'text-error' : 'text-white'
            }`}>
              {daysUntilExpiration > 0 ? `${daysUntilExpiration} dias` : 'Expirado'}
            </span>
          </div>
        </div>

        {/* Warning for expiring soon */}
        {isExpiringSoon && daysUntilExpiration > 0 && (
          <div className="bg-error/10 border border-error/30 rounded-monumenta p-3 mb-4">
            <p className="text-sm text-error font-display">
              Atenção! Seus {balance.daysAvailable} dias de férias expiram em {daysUntilExpiration} dias
            </p>
          </div>
        )}

        {/* Warning for high balance */}
        {hasHighBalance && !isExpiringSoon && (
          <div className="bg-warning/10 border border-warning/30 rounded-monumenta p-3 mb-4">
            <p className="text-sm text-warning font-display">
              Você tem {balance.daysAvailable} dias de férias disponíveis. Planeje suas férias!
            </p>
          </div>
        )}

        {/* Action Button */}
        {onRequestVacation && (
          <button
            onClick={onRequestVacation}
            className="btn-primary w-full"
          >
            <Calendar size={16} />
            Solicitar Férias
          </button>
        )}

        {/* Additional info */}
        {balance.canSellThird && (
          <p className="text-xs text-brand-gray mt-3 text-center">
            Você pode vender até 1/3 das férias (até {Math.floor(totalDays / 3)} dias)
          </p>
        )}
      </div>
    </div>
  )
}

/**
 * Skeleton loader for VacationBalanceCard
 */
export const VacationBalanceCardSkeleton: React.FC = () => {
  return (
    <div className="card animate-pulse">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-2">
          <div className="w-6 h-6 bg-brand-dark-lighter rounded" />
          <div className="w-32 h-6 bg-brand-dark-lighter rounded" />
        </div>
        <div className="w-20 h-4 bg-brand-dark-lighter rounded" />
      </div>

      <div className="text-center mb-6">
        <div className="w-24 h-12 bg-brand-dark-lighter rounded mx-auto mb-2" />
        <div className="w-32 h-5 bg-brand-dark-lighter rounded mx-auto" />
      </div>

      <div className="mb-6">
        <div className="flex justify-between mb-2">
          <div className="w-20 h-4 bg-brand-dark-lighter rounded" />
          <div className="w-10 h-4 bg-brand-dark-lighter rounded" />
        </div>
        <div className="w-full h-3 bg-brand-dark-lighter rounded-full" />
      </div>

      <div className="grid grid-cols-3 gap-4 mb-6">
        {[1, 2, 3].map((i) => (
          <div key={i} className="bg-brand-dark/50 rounded-monumenta p-4">
            <div className="h-6 bg-brand-dark-lighter rounded w-1/2 mb-4"></div>
            <div className="h-20 bg-brand-dark-lighter rounded"></div>
          </div>
        ))}
      </div>

      <div className="bg-brand-dark-lighter/50 rounded-monumenta p-4 mb-4 space-y-2">
        <div className="w-full h-4 bg-brand-dark-lighter rounded" />
        <div className="w-full h-4 bg-brand-dark-lighter rounded" />
      </div>

      <div className="w-full h-12 bg-brand-dark-lighter rounded-monumenta" />
    </div>
  )
}
